import DemographicType from '@/common/enums/DemographicType.enum';
import { Demographic, Organization } from '@/common/models';

const codes = {
  demographic: 'std:',
  predefinedSegment: 'p:',
  age: 'a:',
  gender: 'g:',
};
const orgName = 'Scentre';

function isScentreDemographics(demographic: Demographic, organization: Organization): boolean {
  if (!demographic) return false;
  return (
    demographic.type === DemographicType.Predefined &&
    demographic.organizationId === organization.id &&
    organization.name.includes(orgName)
  );
}

function countCodes(demographic: Demographic): number {
  return [codes.predefinedSegment, codes.age, codes.gender].filter((code) => demographic?.code?.includes(code))?.length;
}

// we display only primary demographic on the frontend. Primary demographics only have ONE of the three (audience, age, gender)
// if it has more, it means it's a combination demographic. We use the selected primary segments to find the combination and send that combination to the BE
function isPrimaryDemographic(demographic: Demographic): boolean {
  return countCodes(demographic) === 1;
}

function isCombination(demographic: Demographic): boolean {
  return countCodes(demographic) > 1;
}

function isPredefinedSegment(demographic: Demographic, organization: Organization): boolean {
  return (
    isScentreDemographics(demographic, organization) &&
    demographic?.code?.startsWith(`${codes.demographic} ${codes.predefinedSegment}`)
  );
}

function isAgeAndGenderDemographic(demographic: Demographic, organization: Organization): boolean {
  return (
    isScentreDemographics(demographic, organization) &&
    demographic?.code?.startsWith(codes.demographic) &&
    !isPredefinedSegment(demographic, organization)
  );
}

function hasTheSamePrimaries(demographic: Demographic, primaryCodes: string[]): boolean {
  const codeCount = countCodes(demographic);

  if (codeCount !== primaryCodes.length) return false;

  const matchingCodes = primaryCodes.filter((c) => demographic.code.includes(c));
  return matchingCodes?.length === primaryCodes.length;
}

function findCombination(combinations: Demographic[], selectedDemographics: Demographic[]): Demographic {
  // we want the codes without the prefix
  const selectedPrimaryCodes = selectedDemographics.map((d) => d.code.split(`${codes.demographic} `)[1]);
  return combinations.find((d) => hasTheSamePrimaries(d, selectedPrimaryCodes));
}

function getPrimariesFromCombination(primaries: Demographic[], combination: Demographic): Demographic[] {
  const primaryCodes = combination.code.split(`${codes.demographic} `)[1].split(' ');
  return primaryCodes.map((c) => primaries.find((d) => d.code === `${codes.demographic} ${c}`));
}

function getPrimaryAgeAndGenderDemographics(demographics: Demographic[], organization: Organization): Demographic[] {
  return demographics.filter((d) => isAgeAndGenderDemographic(d, organization) && isPrimaryDemographic(d));
}

function getPrimaryPredefinedSegments(demographics: Demographic[], organization: Organization): Demographic[] {
  return demographics.filter((d) => isPredefinedSegment(d, organization) && isPrimaryDemographic(d));
}

function getCombinations(demographics: Demographic[], organization: Organization): Demographic[] {
  return demographics?.filter((d) => isScentreDemographics(d, organization) && isCombination(d));
}

function getPrimaries(demographics: Demographic[], organization: Organization): Demographic[] {
  return demographics?.filter((d) => isScentreDemographics(d, organization) && isPrimaryDemographic(d));
}

function canUseScentreDemographics(organization: Organization): boolean {
  return organization?.name?.includes(orgName);
}

function getCodesFromCombinationCode(demographic: Demographic): string[] {
  return demographic.code.split(`${codes.demographic} `)[1].split(' ');
}

const ScentreUtils = {
  codes,
  canUseScentreDemographics,
  getCodesFromCombinationCode,
  getPrimaries,
  getPrimariesFromCombination,
  getPrimaryAgeAndGenderDemographics,
  getPrimaryPredefinedSegments,
  getCombinations,
  isCombination,
  isAgeAndGenderDemographic,
  isPredefinedSegment,
  isPrimaryDemographic,
  isScentreDemographics,
  findCombination,
};

export default ScentreUtils;
